import { importIdentityBundle, type IdentityBundle } from "@beacon/client-core";
import type { HomePlace, Settings } from "../../platform/store.js";

/** Marker tag on Beacon recovery / data-export files. */
export const RECOVERY_KIND = "beacon.recovery.v1";

export interface DataExport {
  kind: typeof RECOVERY_KIND;
  did: string;
  /** The real recovery bundle (manual key-export, mock-register row). */
  identity: IdentityBundle;
  home?: HomePlace;
  locale?: string;
  consents: string[];
  settings: Settings;
  /** Honest seam: full server-side author-scoped event export is a relay capability (D1/D2). */
  note: string;
}

export interface DataExportInput {
  did: string;
  bundle: IdentityBundle;
  home?: HomePlace;
  locale?: string;
  consents?: string[];
  settings?: Settings;
}

const SERVER_EXPORT_NOTE =
  "Includes everything held on this device. Your full authored-event history is a signed request to the relay (arrives with the hosted pilot).";

/** Assemble the portable DSAR export (privacy §6 access/export) from device-held state. */
export function assembleDataExport(i: DataExportInput): DataExport {
  return {
    kind: RECOVERY_KIND,
    did: i.did,
    identity: i.bundle,
    home: i.home,
    locale: i.locale,
    consents: i.consents ?? [],
    settings: i.settings ?? {},
    note: SERVER_EXPORT_NOTE,
  };
}

/** Validate an imported recovery/export file → its IdentityBundle (JWK↔did checked by client-core). */
export async function parseImportedBundle(text: string): Promise<IdentityBundle> {
  let parsed: unknown;
  try { parsed = JSON.parse(text); }
  catch { throw new Error("import: not a valid Beacon recovery file"); }
  const obj = parsed as Record<string, unknown>;
  if (obj?.kind !== RECOVERY_KIND || typeof obj.identity !== "object" || obj.identity === null) {
    throw new Error("import: not a Beacon recovery file");
  }
  const bundle = obj.identity as IdentityBundle;
  await importIdentityBundle(bundle); // throws on JWK↔did mismatch / malformed keys
  return bundle;
}
